/**
 * Pure Meat API Service
 * Handles all API calls to Pure Meat backend
 */

import { API_CONFIG } from '../config/apiConfig';

class PureMeatAPI {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.mediaURL = API_CONFIG.IMAGE_URL;
  }

  /**
   * Make HTTP request with proper headers
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      method: options.method || 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Add authorization header if token exists
    const token = localStorage.getItem('auth_token');
    if (token && !options.skipAuth) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Convert body to URLSearchParams if it's an object
    if (options.body && typeof options.body === 'object') {
      config.body = new URLSearchParams(options.body).toString();
    }

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        console.log('API Error Response:', data);
        console.log('Response status:', response.status);
        throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('API Request failed:', error);
      console.error('Full error object:', error);
      throw error;
    }
  }

  /**
   * Authentication APIs
   */

  /**
   * Login with email and password
   */
  async loginWithEmail(credentials) {
    return this.request('v1/auth/login', {
      body: {
        email: credentials.email,
        password: credentials.password,
        fcm_token: credentials.fcm_token || '',
      },
    });
  }

  /**
   * Send OTP for mobile login
   */
  async sendMobileOTP(mobileData) {
    return this.request('v1/auth/loginWithMobileOtp', {
      body: {
        mobile: mobileData.mobile,
        fcm_token: mobileData.fcm_token || '',
      },
    });
  }

  /**
   * Verify mobile OTP
   */
  async verifyMobileOTP(otpData) {
    return this.request('v1/auth/verifyMobileOtp', {
      body: {
        verification_id: otpData.verification_id,
        otp: otpData.otp,
        fcm_token: otpData.fcm_token || '',
      },
    });
  }

  /**
   * Register new user
   */
  async register(userData) {
    return this.request('v1/auth/create_account', {
      body: {
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
        mobile: userData.mobile || '',
        country_code: '+91',
        password: userData.password,
        fcm_token: userData.fcm_token || 'NA',
        type: 'user',
        lat: '',
        lng: '',
        cover: 'NA',
        others: '',
        date: '',
        stripe_key: 'NA',
        check: false,
        status: 1,
        referral_code: userData.referral || ''
      },
    });
  }

  /**
   * Logout user
   */
  async logout(userId) {
    return this.request('v1/auth/logout', {
      body: {
        id: userId,
      },
    });
  }

  /**
   * Forgot password - send OTP
   */
  async forgotPassword(email) {
    return this.request('v1/auth/forgotPassword', {
      body: {
        email: email,
      },
    });
  }

  /**
   * Verify forgot password OTP
   */
  async verifyForgotPasswordOTP(otpData) {
    return this.request('v1/auth/verify_otp', {
      body: {
        email: otpData.email,
        otp: otpData.otp,
        otp_id: otpData.otp_id,
      },
    });
  }

  /**
   * Reset password
   */
  async resetPassword(resetData) {
    return this.request('v1/auth/reset_password', {
      body: {
        user_id: resetData.user_id,
        password: resetData.password,
        password_confirmation: resetData.password_confirmation,
        reset_token: resetData.reset_token,
      },
    });
  }

  /**
   * Home & Discovery APIs
   */

  /**
   * Search with city
   */
  async searchWithCity(cityId) {
    return this.request('home/searchWithCity', {
      body: { id: cityId },
    });
  }

  /**
   * Search with geolocation
   */
  async searchWithGeoLocation(lat, lng, radius = 20) {
    return this.request('home/searchWithGeoLocation', {
      body: { lat, lng, radius },
    });
  }

  /**
   * Search with ZIP code
   */
  async searchWithZipCode(zipcode) {
    return this.request('home/searchWithZipCode', {
      body: { zipcode },
    });
  }

  /**
   * Categories APIs
   */

  /**
   * Get all categories
   */
  async getCategories() {
    return this.request('categories/getAll');
  }

  /**
   * Get subcategories by category ID
   */
  async getSubCategories(categoryId) {
    return this.request('categories/getSubCategories', {
      body: { id: categoryId },
    });
  }

  /**
   * Products APIs
   */

  /**
   * Search products
   */
  async searchProducts(query, stores = '') {
    return this.request('products/searchQuery', {
      body: {
        param: query,
        stores: stores,
      },
    });
  }

  /**
   * Get product by ID
   */
  async getProductById(productId) {
    return this.request('products/getById', {
      body: { id: productId },
    });
  }

  /**
   * Get products by category
   */
  async getProductsByCategory(categoryId, stores = '', limit = 20, offset = 0) {
    return this.request('products/getByCategoryId', {
      body: {
        id: categoryId,
        stores: stores,
        limit: limit,
        offset: offset,
      },
    });
  }

  /**
   * Get products by subcategory
   */
  async getProductsBySubCategory(subCategoryId, stores = '', limit = 20, offset = 0) {
    return this.request('products/getBySubCategoryId', {
      body: {
        id: subCategoryId,
        stores: stores,
        limit: limit,
        offset: offset,
      },
    });
  }

  /**
   * Get products by store
   */
  async getProductsByStore(storeId, limit = 20, offset = 0) {
    return this.request('products/getByStoreId', {
      body: {
        id: storeId,
        limit: limit,
        offset: offset,
      },
    });
  }

  /**
   * User Profile APIs
   */

  /**
   * Get user profile
   */
  async getUserProfile(userId) {
    return this.request('v1/profile/byId', {
      body: { id: userId },
    });
  }

  /**
   * Update user profile
   */
  async updateUserProfile(profileData) {
    return this.request('v1/profile/update', {
      body: profileData,
    });
  }

  /**
   * Change password
   */
  async changePassword(passwordData) {
    return this.request('v1/profile/change_password', {
      body: passwordData,
    });
  }

  /**
   * Address APIs
   */

  /**
   * Get user addresses
   */
  async getUserAddresses(userId) {
    return this.request('address/getByUid', {
      body: { id: userId },
    });
  }

  /**
   * Add new address
   */
  async addNewAddress(addressData) {
    return this.request('address/addNew', {
      body: addressData,
    });
  }

  /**
   * Update address
   */
  async updateAddress(addressData) {
    return this.request('address/updateMyAddress', {
      body: addressData,
    });
  }

  /**
   * Delete address
   */
  async deleteAddress(addressId) {
    return this.request('address/deleteMyAddress', {
      body: { id: addressId },
    });
  }

  /**
   * Cart APIs
   */

  /**
   * Validate coupon
   */
  async validateCoupon(couponData) {
    return this.request('coupons/validate', {
      body: couponData,
    });
  }

  /**
   * Order APIs
   */

  /**
   * Create order
   */
  async createOrder(orderData) {
    return this.request('orders/create', {
      body: orderData,
    });
  }

  /**
   * Get user orders
   */
  async getUserOrders(userId, limit = 10, offset = 0) {
    return this.request('orders/getByUid', {
      body: {
        id: userId,
        limit: limit,
        offset: offset,
      },
    });
  }

  /**
   * Get order details
   */
  async getOrderDetails(orderId) {
    return this.request('orders/getByOrderId', {
      body: { id: orderId },
    });
  }

  /**
   * Utility APIs
   */

  /**
   * Get app settings
   */
  async getAppSettings() {
    return this.request('settings/getSettings');
  }

  /**
   * Get all coupons
   */
  async getCoupons() {
    return this.request('coupons/getAll');
  }

  /**
   * Get all cities
   */
  async getCities() {
    return this.request('cities/getAll');
  }

  /**
   * Send email verification OTP for registration
   */
  async sendEmailVerificationOTP(emailData) {
    return this.request('v1/sendVerificationOnMail', {
      body: {
        email: emailData.email,
        subject: emailData.subject || 'Verification',
        header_text: emailData.header_text || 'Use this code for verification',
        thank_you_text: emailData.thank_you_text || "Don't share this otp to anybody else",
        mediaURL: emailData.mediaURL || this.mediaURL,
        country_code: emailData.country_code,
        mobile: emailData.mobile
      },
    });
  }

  /**
   * Send contact form message
   */
  async sendContactForm(contactData) {
    const contactResponse = await this.request('v1/contacts/create', {
      body: {
        name: contactData.name || '',
        email: contactData.email || '',
        message: contactData.message || '',
        status: contactData.status || '0',
        date: contactData.date || new Date().toISOString().slice(0, 10),
      },
      skipAuth: true,
    });

    if (contactResponse && contactResponse.status === 200 && contactResponse.data) {
      const adminEmail = contactData.adminEmail || 'contact@tazatropics.com';
      const adminResponse = await this.request('v1/sendMailToAdmin', {
        body: {
          id: contactResponse.data.id,
          mediaURL: contactData.mediaURL || this.mediaURL,
          subject: contactData.subject || 'New Mail Request Received',
          thank_you_text: contactData.thank_you_text || 'You have received new mail',
          header_text: contactData.header_text || 'New Contact Details',
          email: adminEmail,
          from_mail: contactData.email || '',
          from_username: contactData.name || '',
          from_message: contactData.phone
            ? `${contactData.message || ''}\nPhone: ${contactData.phone}`
            : (contactData.message || ''),
          to_respond: contactData.to_respond || 'We have received your request, we will respond on your issue soon',
        },
        skipAuth: true,
      });

      return adminResponse;
    }

    return contactResponse;
  }

  /**
   * Subscribe to newsletter
   */
  async subscribeToNewsletter(emailData) {
    return this.request('v1/users/registerSubscriber', {
      body: {
        email: emailData.email || '',
        timestamp: emailData.timestamp || new Date().toISOString().slice(0, 10),
      },
      skipAuth: true,
    });
  }

  /**
   * Send phone verification OTP for registration
   */
  async sendPhoneVerificationOTP(phoneData) {
    return this.request('verifyPhoneSignup', {
      body: {
        email: phoneData.email,
        country_code: phoneData.country_code,
        mobile: phoneData.mobile
      },
    });
  }

  /**
   * Verify OTP for registration
   */
  async verifyRegistrationOTP(otpData) {
    return this.request('otp/verifyOTP', {
      body: {
        id: otpData.id,
        otp: otpData.otp
      },
    });
  }
}

// Create and export a singleton instance
const pureMeatAPI = new PureMeatAPI();
export default pureMeatAPI;